/**
 * The two site-level pieces of Alert Actions, shared by the site's Alert page and the cloud's
 * (where they are one site's panel):
 *
 *   SentByCard       who sends this site's email / messages — the site, or the cloud. One
 *                    switch for the whole site, which is what keeps the process simple: a
 *                    rule only says WHAT to send.
 *   RecipientsCard   a list of email addresses and message numbers, optionally behind an
 *                    on/off switch, with Save and "Send test". Used for system-alert
 *                    recipients (both pages) and the cloud's AI recipients for a site.
 */
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { BuildingOffice2Icon, CloudIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Callout } from '../common/Callout';
import { AACard, AASegmented, AASwitch, aaInputCls, aaLabelCls } from './AlertActionsControls';
import { alertActionsErrorText, splitRecipients, type HandledBy } from './alertActionsModel';

export function SentByCard({
  value,
  onChange,
  canEdit,
  perspective = 'site',
  cloudAvailable = null,
  cloudReason = null,
}: {
  value: HandledBy | null;
  /** Save the choice; rejects with the reason it could not be changed. */
  onChange: (next: HandledBy) => Promise<unknown>;
  canEdit: boolean;
  /** 'site' on the site's own page; 'central' when the cloud shows one site. */
  perspective?: 'site' | 'central';
  /** Whether the site can reach a cloud. null = unknown. */
  cloudAvailable?: boolean | null;
  cloudReason?: string | null;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const site = perspective === 'site' ? 'This site' : 'The site';

  const pick = async (next: HandledBy) => {
    if (next === value) return;
    setSaving(true);
    setError('');
    try {
      await onChange(next);
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not change who sends notifications'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AACard
      icon={value === 'cloud' ? CloudIcon : BuildingOffice2Icon}
      title="Email & message notifications are sent by"
      subtitle="One choice for every alert of this site — AI analytics, service failures and CPU / memory / disk. Dashboard alerts are always live on the boards; site offline is always sent by the cloud."
      right={
        <AASegmented<HandledBy>
          value={value || 'site'}
          disabled={!canEdit || saving || value === null}
          onChange={pick}
          options={[
            { value: 'site', label: site, icon: BuildingOffice2Icon, hint: "The site's own Email (SMTP) and Message API Server send them" },
            { value: 'cloud', label: 'Cloud', icon: CloudIcon, hint: "The site hands each alert to the cloud, which sends it" },
          ]}
        />
      }
    >
      <p className="text-[12px] text-muted-foreground">
        {value === 'cloud'
          ? perspective === 'site'
            ? "Each alert goes to the cloud, which sends it with its own channels to the rule's recipients plus the people the cloud keeps for this site. This site's channels are not used."
            : "This site hands each alert to the cloud. The cloud sends it to the rule's recipients plus the recipients below."
          : perspective === 'site'
            ? "This site sends each alert itself, with the channels configured on this page. Nothing depends on the cloud."
            : "The site sends its alerts itself, with the channels configured on that site. The recipients below are kept for when you switch to Cloud."}
      </p>
      {value === 'site' && cloudAvailable === false && perspective === 'site' && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">Cloud is unavailable: {cloudReason || 'this site is not connected to a cloud'}.</p>
      )}
      {error && <Callout tone="danger" className="mt-2">{error}</Callout>}
    </AACard>
  );
}

export interface RecipientsValue {
  enabled: boolean;
  email_recipients: string;
  message_api_recipients: string;
}

export function RecipientsCard({
  icon,
  title,
  subtitle,
  value,
  onSave,
  onTest,
  canEdit,
  toggleLabel,
  note,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  subtitle?: ReactNode;
  value: RecipientsValue | null;
  onSave: (next: RecipientsValue) => Promise<unknown>;
  /** "Send test" — resolves with the outcome to show. Omitted = no test button. */
  onTest?: () => Promise<{ ok: boolean; message: string }>;
  canEdit: boolean;
  /** Label of the on/off switch; omitted = always on (no switch). */
  toggleLabel?: string;
  note?: ReactNode;
}) {
  const [form, setForm] = useState<RecipientsValue>({ enabled: true, email_recipients: '', message_api_recipients: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    if (value) setForm(value);
  }, [value]);

  const dirty = !!value && (
    form.enabled !== value.enabled
    || splitRecipients(form.email_recipients).join(',') !== splitRecipients(value.email_recipients).join(',')
    || splitRecipients(form.message_api_recipients).join(',') !== splitRecipients(value.message_api_recipients).join(',')
  );
  const off = !!toggleLabel && !form.enabled;

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      await onSave(form);
      setResult({ tone: 'success', text: 'Saved' });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not save') });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!onTest) return;
    setTesting(true);
    setResult(null);
    try {
      const r = await onTest();
      setResult({ tone: r.ok ? 'success' : 'danger', text: r.message });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Test failed') });
    } finally {
      setTesting(false);
    }
  };

  return (
    <AACard
      icon={icon}
      title={title}
      subtitle={subtitle}
      right={toggleLabel ? (
        <AASwitch
          checked={form.enabled}
          disabled={!canEdit || !value}
          label={toggleLabel}
          onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
        />
      ) : undefined}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span className={`min-w-0 flex-1 text-[12px] ${result?.tone === 'danger' ? 'text-destructive' : 'text-muted-foreground'}`}>
            {result?.text || (dirty ? 'Unsaved changes' : '')}
          </span>
          {onTest && (
            <button
              type="button"
              onClick={test}
              disabled={!canEdit || testing || dirty || !value}
              title={dirty ? 'Save first — the test uses the saved recipients' : 'Send a sample alert to these recipients'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] text-foreground hover:bg-muted disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-3.5 w-3.5" />
              {testing ? 'Sending…' : 'Send test'}
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!canEdit || saving || !dirty}
            className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      {!value ? (
        <div className="h-20 animate-pulse rounded-lg bg-muted" />
      ) : (
        <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 ${off ? 'opacity-50' : ''}`}>
          <label className="block">
            <span className={aaLabelCls}>Email addresses</span>
            <textarea
              rows={2}
              value={form.email_recipients}
              disabled={!canEdit || off}
              onChange={(e) => setForm((f) => ({ ...f, email_recipients: e.target.value }))}
              className={`${aaInputCls} resize-y`}
              placeholder="ops@company.com, it@company.com"
            />
          </label>
          <label className="block">
            <span className={aaLabelCls}>Message API numbers / WhatsApp groups</span>
            <textarea
              rows={2}
              value={form.message_api_recipients}
              disabled={!canEdit || off}
              onChange={(e) => setForm((f) => ({ ...f, message_api_recipients: e.target.value }))}
              className={`${aaInputCls} resize-y`}
              placeholder="919876543210, 120363…@g.us"
            />
          </label>
          {note && <p className="text-[11px] text-muted-foreground md:col-span-2">{note}</p>}
        </div>
      )}
    </AACard>
  );
}
